import type {
    BlockDisplayCondition,
    NormalizedDefaultBlockEntity,
    NormalizedSpecificBlockEntity,
    NormalizedZeroBlockEntity,
} from '@web/types';

import { compactBlockProps, createDefaultBlock, createSpecificBlock, createZeroBlock } from '@sites/entity-core';

import { getUuid } from '@common/helpers';
import { ThemeColorNames } from '@sites/site-theme/src/enums/color-names.enum';
import { EHorizontalPosition, ESize } from '@common/enums';
import {
    EDisplayState,
    EDisplayConditionOperator,
    EDisplayConditionType,
    EEqualityType,
} from '@common/enums/display-condition.enum';

export const BLOCK_DISPLAY_CONDITION_DEFAULT: BlockDisplayCondition = {
    state: EDisplayState.VISIBLE,
    operator: EDisplayConditionOperator.AND,
    conditions: [
        {
            conditionType: EDisplayConditionType.ALWAYS,
            equality: EEqualityType.EQUAL,
            value: true,
        },
    ],
};

type BlockSeed = {
    /** Section ids. */
    children?: string[];
};

/**
 * The seeds used to write the same values into all three device bags. v0 has one
 * responsive leaf per property, so that repetition collapses here — a factory
 * rather than a value, so the three keys never alias one object.
 */
const everywhere = <T>(make: () => T) => ({ desktop: make(), tablet: make(), mobile: make() });

const px = (value: number) => ({ value: { top: value, right: 0, bottom: value, left: 0 }, unit: 'px' as const });

const themeColor = (): string => ThemeColorNames.light;

/**
 * Seeds come from `@sites/entity-core` so the store and the schema the entity is
 * validated against cannot drift apart — only identity, the child list and the
 * values that reference a host enum are filled in here.
 */
export const blockInstance = ({ children = [] }: BlockSeed = {}): NormalizedDefaultBlockEntity => {
    const block = createDefaultBlock();

    return {
        ...block,
        id: getUuid(),
        children,
        // The contour, the dividers and the background layers are off, so they keep
        // their switches alone; switching one on seeds it from entity-core.
        props: compactBlockProps({
            ...block.props,
            padding: everywhere(() => px(100)),
            background: { ...block.props.background, color: everywhere(themeColor) },
        }),
    };
};

export const specificBlockInstance = ({ children = [] }: BlockSeed = {}): NormalizedSpecificBlockEntity => {
    const block = createSpecificBlock();

    return {
        ...block,
        id: getUuid(),
        children,
        meta: { ...block.meta, role: 'header', isShared: true },
        props: compactBlockProps({
            ...block.props,
            // The header sits flush on desktop and gets breathing room once it
            // collapses into the burger.
            padding: {
                desktop: { value: { top: 0, right: 0, bottom: 0, left: 0 }, unit: 'px' },
                tablet: { value: { top: 15, right: 15, bottom: 15, left: 15 }, unit: 'px' },
                mobile: { value: { top: 15, right: 15, bottom: 15, left: 15 }, unit: 'px' },
            },
            background: { ...block.props.background, color: everywhere(themeColor) },
            icon: {
                horizontalPosition: EHorizontalPosition.R,
                size: ESize.M,
                color: '#333',
            },
            textColor: '#333',
            horizontalPosition: EHorizontalPosition.L,
            hideContentOnMobile: true,
            hasSeparator: false,
            hideWidgetsOnMobile: {},
        }),
    };
};

export const zeroBlockInstance = ({ children = [] }: BlockSeed = {}): NormalizedZeroBlockEntity => {
    const block = createZeroBlock();

    return {
        ...block,
        id: getUuid(),
        children,
        props: compactBlockProps({
            ...block.props,
            background: { ...block.props.background, color: everywhere(themeColor) },
            height: everywhere(() => ({ value: 600, unit: 'px' as const })),
            hideOverflow: true,
        }),
    };
};
